<template>
    <div class="news-page">
        <PageHeader title="企业动态" subtitle="了解沃雷顿自行车的最新资讯和行业动态" currentText="企业动态" />

        <!-- 降级数据提示 -->
        <el-alert v-if="isFallback" :title="errorMessage" type="info" :closable="false" center show-icon
            style="margin-bottom: 20px;" />

        <div class="page-container">
            <!-- 分类筛选 -->
            <div class="category-filter">
                <el-radio-group v-model="selectedCategory" @change="handleCategoryChange">
                    <el-radio-button :label="null">全部分类</el-radio-button>
                    <el-radio-button v-for="category in categories" :key="category.id" :label="category.id">
                        {{ category.name }}
                    </el-radio-button>
                </el-radio-group>
            </div>

            <!-- 新闻列表 -->
            <div class="news-list" v-loading="loading">
                <div class="row">
                    <div class="col" v-for="item in data" :key="item._id">
                        <ArticleCard :id="item._id" :title="item.title" :summary="getSummary(item.content)"
                            :cover="item.cover" :date="item.editTime" :category="getCategoryName(item.category)"
                            type="news" @view="viewDetail" />
                    </div>
                </div>

                <!-- 空状态 -->
                <el-empty v-if="!loading && data.length === 0" description="暂无相关新闻" />
            </div>

            <!-- 分页 -->
            <Pagination v-if="total > 0" :total="total" :current-page="currentPage" :page-size="pageSize"
                @current-change="fetchNews" @size-change="fetchNews" />
        </div>
    </div>
</template>

<script>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { getNewsList, getNewsCategories } from '@/api/news';
import { usePagination } from '@/utils/useApi';
import { getSummary } from '@/utils/helper';
import PageHeader from '@/components/PageHeader.vue';
import ArticleCard from '@/components/ArticleCard.vue';
import Pagination from '@/components/Pagination.vue';

export default {
    name: 'NewsView',
    components: {
        PageHeader,
        ArticleCard,
        Pagination
    },
    setup() {
        const router = useRouter();
        const selectedCategory = ref(null);
        const categories = ref([]);
        const pageSize = ref(10);
        const currentPage = ref(1);
        const total = ref(0);
        const isFallback = ref(false);
        const errorMessage = ref('');

        // 降级数据 - 新闻分类
        const fallbackCategories = [
            { id: 1, name: '公司新闻' },
            { id: 2, name: '行业动态' },
            { id: 3, name: '技术洞察' }
        ];

        // 降级数据 - 新闻列表
        const data = ref([]);
        const loading = ref(false);

        // 获取新闻分类
        const fetchCategories = async () => {
            try {
                const res = await getNewsCategories();
                if (res.data && res.data.code === 200) { 
                    categories.value = res.data?.rows.map(item => {
                        return {
                            id: item.dictValue,
                            name: item.dictLabel,
                        }
                    })

                }
            } catch (error) {
                console.error('获取新闻分类失败:', error);
                isFallback.value = true;
            }
        };

        // 获取分类名称
        const getCategoryName = (categoryId) => {
            if (!categoryId) return '未分类';
            const category = categories.value.find(c => c.id === categoryId);
            return category ? category.name : '未分类';
        };

        // 获取新闻列表
        const fetchNews = async () => {
            loading.value = true;
            try {
                const res = await getNewsList({
                    pageNum: currentPage.value,
                    pageSize: pageSize.value,
                    noticeType: selectedCategory.value
                });
                if (res && res.data) {
                    total.value = res.data?.total;
                    data.value = res.data?.rows.map(item => {
                        return {
                            _id: item.noticeId,
                            title: item.noticeTitle,
                            content: item.subtitle,
                            cover: item.pic || '',
                            category : item.noticeType,
                            editTime: item.publishTime || new Date().toISOString()
                        }
                    })
                }

            } catch (error) {
                console.error('获取新闻失败:', error);
                // 使用降级数据
                data.value = [];
            } finally {
                loading.value = false;
            }
        };

        // 查看详情
        const viewDetail = (id) => {
            router.push(`/news/${id}`);
        };

        // 处理分类变化
        const handleCategoryChange = () => {
            fetchNews();
        };

        // 监听分类变化
        watch(selectedCategory, () => {
            handleCategoryChange();
        });

        onMounted(() => {
            fetchCategories();
            fetchNews();
        });

        return {
            data,
            loading,
            currentPage,
            total,
            selectedCategory,
            categories,
            pageSize,
            getCategoryName,
            getSummary,
            viewDetail,
            handleCategoryChange,
        };
    }
}
</script>

<style lang="scss" scoped>
.news-page {
    .category-filter {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: 30px;
        justify-items: center;
    }

    .news-list {
        margin-bottom: 30px;
        min-height: 300px;

        .row {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 30px;

            @media (max-width: 1200px) {
                grid-template-columns: repeat(2, 1fr);
            }

            @media (max-width: 768px) {
                grid-template-columns: 1fr;
            }

            .col {
                margin-bottom: 30px;
            }
        }
    }
}
</style>